import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Box, Container, Typography } from "@mui/material";
import { JourneyProgressStepper } from "../../commanComponents/JourneyProgressStepper";
import { PageTitle } from "../../commanComponents/PageTitle";
import { String } from "../../constants/Strings";
import { accordionSx, colors, fonts } from "../../style";

import {
  DepositDetailsModal,
  NpsDetailsModal,
  PolicyDetailsModal,
  ProvidentFundDetailsModal,
  SnapshotCollectionListModal,
  formatDayMonthYear,
} from "../../commanComponents/SnapshotDetailsModals";

import { useLocation, useNavigate } from "react-router-dom";
import {
  Accordion,
  Button,
  SimpleInput,
} from "@axismaxlife/unified-ui-components-library";
import { FinancialCollectionRow } from "../../commanComponents/FinancialCollectionRow";
import { useFinancialSnapshot } from "../../api/apiEndpoints/FinancialSnapshot";
import { AppToast } from "../../commanComponents/AppToast";
import { AuthContext } from "../../Hooks/context/AuthContext";

import { AccountAagregatorContext } from "../../Hooks/context/AccountAaggregator";
import { DataContext } from "../../Hooks/context/ChildData";
import { OTPVerifyContext } from "../../Hooks/context/OTPVeriryContext";
import { gaCustomEvent } from "../../logic/CommonFunctions";
import { formatToIndianCurrency } from "../../logic/CurrencyFormatter";
import { amountInWords } from "../../logic/NumberToWord";

const SNAPSHOT_FIELDS = String.Dashboard.financialsnapshotFeilds;
const SNAPSHOT_COPY = String.Dashboard.snapshot;

const sumBy = (list, key) =>
  (Array.isArray(list) ? list : []).reduce((total, item) => {
    const amount = Number(item && item[key]);
    return Number.isFinite(amount) ? total + amount : total;
  }, 0);

const upsertAt = (list, entry, index) => {
  const current = Array.isArray(list) ? list : [];
  if (index === null || index === undefined || index < 0) {
    return [...current, entry];
  }
  return current.map((item, position) => (position === index ? entry : item));
};

const removeAt = (list, index) =>
  (Array.isArray(list) ? list : []).filter((_, position) => position !== index);

const normalizeDateToHyphen = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr instanceof Date) {
    return formatDayMonthYear(dateStr);
  }
  return `${dateStr}`.trim().replace(/\//g, "-");
};

const normalizePfList = (rawPfList, rawPFObj) => {
  const extractYear = (val) => {
    if (!val) return 0;
    if (typeof val === "number") return val;
    const str = `${val}`.trim();
    if (/^\d{4}$/.test(str)) return Number(str);
    const match = str.match(/\b(19\d\d|20\d\d)\b/);
    if (match) return Number(match[1]);
    return Number(str) || 0;
  };

  if (Array.isArray(rawPfList) && rawPfList.length > 0) {
    return rawPfList
      .filter((item) => Number(item?.totalAmount) > 0)
      .map((item) => ({
        pfType: item.pfType || item.fundType || "PPF",
        totalAmount: Number(item.totalAmount) || 0,
        tenure: extractYear(item.tenure || item.maturityDate),
      }));
  }
  if (rawPFObj && typeof rawPFObj === "object") {
    const list = [];
    if (Number(rawPFObj.EPF?.totalAmount) > 0) {
      list.push({
        pfType: "EPF",
        totalAmount: Number(rawPFObj.EPF.totalAmount) || 0,
        tenure: extractYear(rawPFObj.EPF.tenure || rawPFObj.EPF.maturityDate),
      });
    }
    if (Number(rawPFObj.PPF?.totalAmount) > 0) {
      list.push({
        pfType: "PPF",
        totalAmount: Number(rawPFObj.PPF.totalAmount) || 0,
        tenure: extractYear(rawPFObj.PPF.tenure || rawPFObj.PPF.maturityDate),
      });
    }
    return list;
  }
  return [];
};

const normalizeServerFinancialValues = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  const { pFList, pfList, npsList, policies, deposits, ...rest } = raw;
  const normalizedPf = normalizePfList(pfList, pFList);
  const normalizedNps = (Array.isArray(npsList) ? npsList : [])
    .filter((item) => Number(item?.totalHolding) > 0)
    .map((item) => ({
      totalHolding: Number(item.totalHolding) || 0,
      maturityDate: normalizeDateToHyphen(item.maturityDate),
    }));
  const computedPfTotal = normalizedPf.reduce(
    (sum, item) => sum + (Number(item.totalAmount) || 0),
    0,
  );
  const computedNpsTotal = normalizedNps.reduce(
    (sum, item) => sum + (Number(item.totalHolding) || 0),
    0,
  );
  return {
    ...rest,
    policies: Array.isArray(policies) ? policies : [],
    deposits: Array.isArray(deposits) ? deposits : [],
    pfList: normalizedPf,
    npsList: normalizedNps,
    providentFundAmount: Number(rest.providentFundAmount) || computedPfTotal,
    totalNpsAmount: Number(rest.totalNpsAmount) || computedNpsTotal,
  };
};

const FinancialSnapshot = () => {
  const [editIndex, setEditIndex] = useState(null);
  const [editPFKey, setEditPFKey] = useState(null);
  const [financialValues, setFinancialValues] = useState({
    deposits: [],
    policies: [],
    pfList: [],
    npsList: [],
    totalDepositAmount: 0,
    totalNpsAmount: 0,
    providentFundAmount: 0,
    totalPolicyAmount: 0,
    totalBankBalanceAmount: "",
    totalMutualFundsAmount: "",
    totalStocksAmount: "",
    avgExpenses: "",
    loanAmount: "",
    emiAmount: "",
    totalAssetAmount: 0,
    monthlyIncomeAmount: 0,
  });
  const [activeModal, setActiveModal] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState({
    message: "",
    severity: "",
  });
  const [, setErrorFields] = useState(false);

  const navigation = useNavigate();
  const location = useLocation();
  const locationcheck = location.state?.replace;

  const hasFetched = useRef(false);
  const { enterManually } = useContext(DataContext);
  const { authTokens } = useContext(AuthContext);
  const { verifyData } = useContext(OTPVerifyContext);
  const { userId } = authTokens;
  const { collectedAccountAggregatorData, collectAccountAggregatorData } =
    useContext(AccountAagregatorContext);
  const { saveFinancialSnapshot, getFinancialSnapshot } =
    useFinancialSnapshot();

  useEffect(() => {
    const fetchFinancialSnapshot = async () => {
      if (!userId) return;
      try {
        const response = await getFinancialSnapshot({ userId });

        if (response.httpCode === 200) {
          if (response.response && typeof response.response === "object") {
            setFinancialValues(
              normalizeServerFinancialValues(response.response),
            );
          } else {
            setFinancialValues({});
          }
        } else {
          setSnackbarMessage({
            message:
              String.Dashboard.FinancialSnapshotMessages.fetchDataFromServer,
            severity: String.ErrorSeverity,
          });
          setSnackbarOpen(true);
        }
      } catch (error) {
        setSnackbarMessage({
          message:
            String.Dashboard.FinancialSnapshotMessages.fetchDataFromServer,
          severity: String.ErrorSeverity,
        });
        setSnackbarOpen(true);
      }
    };

    if (!enterManually && !hasFetched.current) {
      fetchFinancialSnapshot();
      hasFetched.current = true;
    }
  }, []);

  useEffect(() => {
    const fetchAccountAagregator = async () => {
      if (collectedAccountAggregatorData.httpCode === 200) {
        setSnackbarOpen(true);
        setSnackbarMessage({
          message:
            String.Dashboard.FinancialSnapshotMessages
              .accountDetailsFetchSuccessfully,
          severity: String.successSeverity,
        });
        setFinancialValues(
          normalizeServerFinancialValues(
            collectedAccountAggregatorData.response || {},
          ),
        );
      } else {
      }
    };
    if (locationcheck) {
      fetchAccountAagregator();
    }
  }, [
    collectAccountAggregatorData.httpCode,
    collectAccountAggregatorData.response,
    collectedAccountAggregatorData,
    locationcheck,
  ]);

  const totalAssetAmount = useMemo(() => {
    const relevantKeys = [
      String.Dashboard.financialsnapshotFeilds.totalDepositAmount,
      String.Dashboard.financialsnapshotFeilds.totalNpsAmount,
      String.Dashboard.financialsnapshotFeilds.providentFundAmount,
      String.Dashboard.financialsnapshotFeilds.totalBankBalanceAmount,
      String.Dashboard.financialsnapshotFeilds.totalMutualFundsAmount,
      String.Dashboard.financialsnapshotFeilds.totalStocksAmount,
    ];

    return relevantKeys.reduce((acc, key) => {
      const value = Number(financialValues[key]) || 0;
      return acc + value;
    }, 0);
  }, [financialValues]);

  const requiredSnapshotFields = useMemo(
    () => [
      SNAPSHOT_FIELDS.totalBankBalanceAmount,
      SNAPSHOT_FIELDS.totalPolicyAmount,
      SNAPSHOT_FIELDS.avgExpenses,
    ],
    [],
  );

  const isSnapshotComplete = useMemo(
    () =>
      requiredSnapshotFields.every(
        (field) =>
          financialValues[field] !== "" &&
          financialValues[field] !== null &&
          financialValues[field] !== undefined &&
          Number(financialValues[field]) > 0,
      ),
    [financialValues, requiredSnapshotFields],
  );

  const deposits = Array.isArray(financialValues.deposits)
    ? financialValues.deposits
    : [];
  const policies = Array.isArray(financialValues.policies)
    ? financialValues.policies
    : [];
  const npsList = Array.isArray(financialValues.npsList)
    ? financialValues.npsList
    : [];

  const pfCount = Array.isArray(financialValues.pfList)
    ? financialValues.pfList.filter((item) => Number(item?.totalAmount) > 0)
      .length
    : 0;

  const npsCount = Array.isArray(financialValues.npsList)
    ? financialValues.npsList.filter((item) => Number(item?.totalHolding) > 0)
      .length
    : (Number(financialValues.totalNpsAmount) > 0 ? 1 : 0);

  const depositEntries = deposits.map((deposit) => ({
    title: deposit.depositName || SNAPSHOT_COPY.modals.deposit.summaryTitle,
    rows: [
      {
        leftLabel: SNAPSHOT_COPY.modals.deposit.amountLabel,
        leftValue: formatToIndianCurrency(deposit.totalAmount || 0),
        rightLabel: SNAPSHOT_COPY.modals.deposit.dateLabel,
        rightValue: deposit.maturityDate || "-",
      },
    ],
  }));

  const policyEntries = policies.map((policy) => {
    const premiumVal = Number(policy.yearlyPremium);
    const hasPremium = !isNaN(premiumVal) && premiumVal > 0;
    return {
      title: policy.policyType || SNAPSHOT_FIELDS.policyTitle,
      subtitle: hasPremium
        ? `${SNAPSHOT_COPY.modals.policy.premiumLabel || "Yearly premium"} : ${formatToIndianCurrency(premiumVal)}`
        : "",
      rows: [
        {
          leftLabel: SNAPSHOT_COPY.modals.policy.coverageLabel,
          leftValue: formatToIndianCurrency(policy.coverageAmount || 0),
          rightLabel: SNAPSHOT_COPY.modals.policy.date,
          rightValue: policy.maturityDate || "-",
        },
      ],
    };
  });

  const pfEntries = useMemo(() => {
    const list = Array.isArray(financialValues.pfList)
      ? financialValues.pfList
      : [];
    return list
      .filter((item) => Number(item?.totalAmount) > 0)
      .map((item, idx) => {
        const isEpf = item.pfType === "EPF";
        const isPpf = item.pfType === "PPF";
        const amountLabel = isEpf
          ? SNAPSHOT_COPY.modals.providentFund.epfAmount || "EPF amount"
          : isPpf
            ? SNAPSHOT_COPY.modals.providentFund.ppfAmount || "PPF amount"
            : SNAPSHOT_COPY.modals.providentFund.amount || "Total Amount";

        return {
          fundKey: item.pfType || idx,
          title: item.pfType || "Provident Fund",
          rows: [
            {
              leftLabel: amountLabel,
              leftValue: formatToIndianCurrency(item.totalAmount || 0),
              rightLabel: SNAPSHOT_COPY.modals.providentFund.maturityYear,
              rightValue: item.tenure ? `${item.tenure}` : "-",
            },
          ],
        };
      });
  }, [financialValues.pfList]);

  const handleOpenDeposit = useCallback(() => {
    setEditIndex(null);
    if (deposits.length > 0) {
      setActiveModal("depositList");
    } else {
      setActiveModal("depositForm");
    }
  }, [deposits.length]);

  const handleCloseDepositForm = useCallback(() => {
    setEditIndex(null);
    if (deposits.length > 0) {
      setActiveModal("depositList");
    } else {
      setActiveModal(null);
    }
  }, [deposits.length]);

  const handleOpenPolicy = useCallback(() => {
    setEditIndex(null);
    if (policies.length > 0) {
      setActiveModal("policyList");
    } else {
      setActiveModal("policyForm");
    }
  }, [policies.length]);

  const handleClosePolicyForm = useCallback(() => {
    setEditIndex(null);
    if (policies.length > 0) {
      setActiveModal("policyList");
    } else {
      setActiveModal(null);
    }
  }, [policies.length]);

  const amountField = (field, label) => {
    const rawValue = financialValues[field];
    const displayValue =
      rawValue === 0 || rawValue === "0" || !rawValue
        ? ""
        : `${rawValue}`.replace(/[^0-9]/g, "");
    return (
      <Box sx={{ mt: "16px" }}>
        <Typography
          sx={{
            mb: "8px",
            fontSize: "16px",
            fontFamily: fonts.notoSans,
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        <SimpleInput
          name={field}
          type="number"
          indianNumberFormat
          prefix="₹"
          width="100%"
          label={String.Monthlyincome.amountPlaceholder}
          placeholder={String.Monthlyincome.amountPlaceholder}
          value={displayValue}
          hintText={displayValue ? amountInWords(displayValue) : ""}
          onChange={(e) =>
            onHandleChange(field, e.target.value.replace(/[^0-9]/g, ""))
          }
        />
      </Box >
    );
  };
  const onHandleChange = useCallback((key, value) => {
    setFinancialValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleViewDashbord = async () => {
    gaCustomEvent(
      String.gaEventName.acrPortfolio,
      String.gaEventClickName.acrPortfolioFinancialSnapshotViewDashboard,
      verifyData.customerId,
    );
    const requiredFields = [
      String.Dashboard.financialsnapshotFeilds.totalBankBalanceAmount,
      String.Dashboard.financialsnapshotFeilds.totalPolicyAmount,
      String.Dashboard.financialsnapshotFeilds.avgExpenses,
    ];

    const errors = {};

    requiredFields.forEach((field) => {
      if (financialValues[field] === "" || !financialValues[field]) {
        errors[field] = true;
      }
    });

    if (Object.keys(errors).length > 0) {
      setErrorFields(true);
      const errorMessages = Object.keys(errors).map((field) => {
        switch (field) {
          case String.Dashboard.financialsnapshotFeilds.totalBankBalanceAmount:
            return String.Dashboard.mainDashboard.BankAmount;
          case String.Dashboard.financialsnapshotFeilds.totalPolicyAmount:
            return String.Dashboard.mainDashboard.PolicyAmount;
          case String.Dashboard.financialsnapshotFeilds.avgExpenses:
            return String.Dashboard.mainDashboard.AvgExpenseAmount;
          default:
            return "";
        }
      });
      setSnackbarMessage({
        message: errorMessages.join(", "),
        severity: String.ErrorSeverity,
      });
      setSnackbarOpen(true);
      return;
    } else {
      setErrorFields(false);
    }

    if (
      collectedAccountAggregatorData?.response?.policies &&
      collectedAccountAggregatorData.response.policies.length > 0
    ) {
      const missingFields =
        collectedAccountAggregatorData.response.policies.filter((policy) => {
          return !policy.policyType || policy.policyType === "";
        });

      if (missingFields.length > 0) {
        const updatedPolicies = financialValues.policies || [];
        const stillMissingFields = updatedPolicies.filter((policy) => {
          return !policy.policyType || policy.policyType === "";
        });

        if (stillMissingFields.length > 0) {
          setSnackbarOpen(true);
          setSnackbarMessage({
            message:
              String.Dashboard.FinancialSnapshotMessages.EnterMissingPolicy,
            severity: String.ErrorSeverity,
          });
          return;
        }
      }
    }
    const normalizedPolicies = (
      Array.isArray(financialValues.policies) ? financialValues.policies : []
    ).map((policy) => ({
      policyType: policy.policyType || "",
      coverageAmount: policy.coverageAmount ?? "",
      maturityDate: normalizeDateToHyphen(policy.maturityDate),
      yearlyPremium: policy.yearlyPremium ?? "",
    }));

    const normalizedPfList = (
      Array.isArray(financialValues.pfList) ? financialValues.pfList : []
    )
      .filter((item) => Number(item?.totalAmount) > 0)
      .map((item) => ({
        pfType: item.pfType || "PPF",
        totalAmount: Number(item.totalAmount) || 0,
        tenure: Number(item.tenure) || 0,
      }));

    const normalizedNpsList = (
      Array.isArray(financialValues.npsList) ? financialValues.npsList : []
    )
      .filter((item) => Number(item?.totalHolding) > 0)
      .map((item) => ({
        totalHolding: Number(item.totalHolding) || 0,
        maturityDate: normalizeDateToHyphen(item.maturityDate),
      }));

    const normalizedDeposits = (
      Array.isArray(financialValues.deposits) ? financialValues.deposits : []
    ).map((deposit) => ({
      depositName: deposit.depositName || "",
      totalAmount: deposit.totalAmount ?? "",
      maturityDate: normalizeDateToHyphen(deposit.maturityDate),
    }));

    const data = {
      userId,
      policies: normalizedPolicies,
      pfList: normalizedPfList,
      npsList: normalizedNpsList,
      deposits: normalizedDeposits,
      totalAssetAmount: totalAssetAmount,
      monthlyIncomeAmount: Number(financialValues.monthlyIncomeAmount) || 0,
      totalBankBalanceAmount:
        Number(financialValues.totalBankBalanceAmount) || 0,
      totalDepositAmount: Number(financialValues.totalDepositAmount) || 0,
      totalMutualFundsAmount: financialValues.totalMutualFundsAmount ?? "0",
      totalStocksAmount: financialValues.totalStocksAmount ?? "0",
      totalPolicyAmount: Number(financialValues.totalPolicyAmount) || 0,
      avgExpenses: Number(financialValues.avgExpenses) || 0,
      loanAmount: financialValues.loanAmount ?? "0",
      emiAmount: financialValues.emiAmount ?? "0",
      providentFundAmount: Number(financialValues.providentFundAmount) || 0,
      totalNpsAmount: financialValues.totalNpsAmount || 0.0,
    };

    try {
      const response = await saveFinancialSnapshot(data);

      if (response.httpCode === 200) {
        setSnackbarMessage({
          message:
            String.Dashboard.FinancialSnapshotMessages
              .financialSnapshotSaveSuccess,
          severity: String.successSeverity,
        });
        setSnackbarOpen(true);

        navigation(String.navigations.customerdashboard);
      } else {
        setSnackbarMessage({
          message:
            String.Dashboard.FinancialSnapshotMessages
              .financialSnapshotSaveError,
          severity: String.ErrorSeverity,
        });
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage({
        message:
          String.Dashboard.FinancialSnapshotMessages.ErrorWhileSavingData,
        severity: String.ErrorSeverity,
      });
      setSnackbarOpen(true);
    }
  };

  const writeDeposits = useCallback((deposits) => {
    setFinancialValues((prev) => ({
      ...prev,
      deposits,
      totalDepositAmount: sumBy(deposits, "totalAmount"),
    }));
  }, []);

  const writePolicies = useCallback((policies) => {
    setFinancialValues((prev) => ({
      ...prev,
      policies,
      totalPolicyAmount: sumBy(policies, "coverageAmount"),
    }));
  }, []);

  const writeNps = useCallback((npsList) => {
    setFinancialValues((prev) => ({
      ...prev,
      npsList,
      totalNpsAmount: sumBy(npsList, "totalHolding"),
    }));
  }, []);

  const handleDepositSubmit = (entry) => {
    writeDeposits(upsertAt(financialValues.deposits, entry, editIndex));
    setEditIndex(null);
    setActiveModal("depositList");
  };

  const handlePolicySubmit = (entry) => {
    writePolicies(upsertAt(financialValues.policies, entry, editIndex));
    setEditIndex(null);
    setActiveModal("policyList");
  };

  const handleNPSSubmit = (entry) => {
    writeNps([entry]);
    setActiveModal(null);
  };

  const handleOpenPF = useCallback(() => {
    setEditPFKey(null);
    if (pfEntries.length > 0) {
      setActiveModal("pfList");
    } else {
      setActiveModal("pfForm");
    }
  }, [pfEntries.length]);

  const handleClosePFForm = useCallback(() => {
    setEditPFKey(null);
    if (pfEntries.length > 0) {
      setActiveModal("pfList");
    } else {
      setActiveModal(null);
    }
  }, [pfEntries.length]);

  const handleClosePFList = useCallback(() => {
    setActiveModal(null);
  }, []);

  const handleAddAnotherPF = () => {
    const currentList = Array.isArray(financialValues.pfList)
      ? financialValues.pfList
      : [];
    const hasPPF = currentList.some((item) => (item.pfType || item.fundKey) === "PPF");
    const hasEPF = currentList.some((item) => (item.pfType || item.fundKey) === "EPF");
    if (hasPPF && !hasEPF) {
      setEditPFKey("EPF");
    } else if (hasEPF && !hasPPF) {
      setEditPFKey("PPF");
    } else {
      setEditPFKey("EPF");
    }
    setActiveModal("pfForm");
  };

  const handleEditPF = (index) => {
    const entry = pfEntries[index];
    if (entry) {
      setEditPFKey(entry.fundKey);
    }
    setActiveModal("pfForm");
  };

  const handleDeletePF = (index) => {
    const entry = pfEntries[index];
    if (!entry) return;
    const keyToDelete = entry.fundKey;
    const currentList = Array.isArray(financialValues.pfList)
      ? financialValues.pfList
      : [];
    const updatedPfList = currentList.filter(
      (item) => (item.pfType || item.fundKey) !== keyToDelete,
    );
    const newTotal = updatedPfList.reduce(
      (sum, item) => sum + (Number(item.totalAmount) || 0),
      0,
    );
    setFinancialValues((prev) => {
      const { pFList, ...rest } = prev;
      return {
        ...rest,
        pfList: updatedPfList,
        providentFundAmount: newTotal,
      };
    });
    if (updatedPfList.length === 0) {
      setActiveModal(null);
    }
  };

  const handlePFSubmit = (data) => {
    let newPfList = [];
    if (Array.isArray(data)) {
      newPfList = data;
    } else if (data && typeof data === "object") {
      newPfList = [
        ...(Number(data.EPF?.totalAmount) > 0
          ? [
            {
              pfType: "EPF",
              totalAmount: Number(data.EPF.totalAmount) || 0,
              tenure: Number(data.EPF.tenure) || 0,
            },
          ]
          : []),
        ...(Number(data.PPF?.totalAmount) > 0
          ? [
            {
              pfType: "PPF",
              totalAmount: Number(data.PPF.totalAmount) || 0,
              tenure: Number(data.PPF.tenure) || 0,
            },
          ]
          : []),
      ];
    }
    const newTotal = newPfList.reduce(
      (sum, item) => sum + (Number(item.totalAmount) || 0),
      0,
    );
    setFinancialValues((prev) => {
      const { pFList, ...rest } = prev;
      return {
        ...rest,
        pfList: newPfList,
        providentFundAmount: newTotal,
      };
    });
    setEditPFKey(null);
    setActiveModal("pfList");
  };

  const handleAddAnotherDeposit = () => {
    setEditIndex(null);
    setActiveModal("depositForm");
  };

  const handleEditDeposit = (index) => {
    setEditIndex(index);
    setActiveModal("depositForm");
  };

  const handleDeleteDeposit = (index) => {
    const next = removeAt(deposits, index);
    writeDeposits(next);
    if (next.length === 0) {
      setActiveModal(null);
    }
  };

  const handleCloseDepositList = () => {
    setActiveModal(null);
  };

  const handleAddAnotherPolicy = () => {
    setEditIndex(null);
    setActiveModal("policyForm");
  };

  const handleEditPolicy = (index) => {
    setEditIndex(index);
    setActiveModal("policyForm");
  };

  const handleDeletePolicy = (index) => {
    const next = removeAt(policies, index);
    writePolicies(next);
    if (next.length === 0) {
      setActiveModal(null);
    }
  };

  const handleClosePolicyList = () => {
    setActiveModal(null);
  };

  const handleOpenNPS = () => {
    setActiveModal("nps");
  };

  const handleCloseNPS = () => {
    setActiveModal(null);
  };

  return (
    <Box
      sx={{
        height: "100vh",
      }}
    >
      <Container sx={{ flexGrow: 1 }}>
        <PageTitle />
        <JourneyProgressStepper />
      </Container>
      <Container sx={{ pb: "16px" }}>
        <Box
          sx={{
            mt: "16px",
            borderRadius: "16px",
            borderWidth: "1px",
            borderColor: colors.softGray,
            borderStyle: "solid",
            overflow: "hidden",
            ...accordionSx,
          }}
        >
          <Accordion
            width="100%"
            defaultOpenIndex={0}
            items={[
              {
                title:
                  <Typography
                    component="span"
                    sx={{
                      color: colors.ColorpalettePrimaryMidnight,
                      fontSize: "18px",
                      fontWeight: 500,
                      fontFamily: fonts.notoSans,
                    }}
                  >
                    {String.Dashboard.snapshot.expensesAccordion}
                  </Typography>,
                children: (
                  <Box sx={{ pb: "20px" }}>
                    {amountField(
                      "avgExpenses",
                      String.Dashboard.snapshot.avgExpenses,
                    )}
                    {amountField("loanAmount", String.Dashboard.snapshot.loans)}
                    {amountField("emiAmount", String.Dashboard.snapshot.emi)}
                  </Box>
                ),
              },
            ]}

          />
        </Box>

        <Box
          sx={{
            mt: "16px",
            borderRadius: "16px",
            borderWidth: "1px",
            borderColor: colors.softGray,
            borderStyle: "solid",
            overflow: "hidden",
            ...accordionSx,
          }}
        >
          <Accordion
            width="100%"
            defaultOpenIndex={0}
            items={[
              {

                title:
                  <Typography
                    component="span"
                    sx={{
                      color: colors.ColorpalettePrimaryMidnight,
                      fontSize: "18px",
                      fontWeight: 500,
                      fontFamily: fonts.notoSans,
                    }}
                  >
                    {String.Dashboard.snapshot.investmentsAccordion}
                  </Typography>,
                children: (
                  <Box sx={{ pb: "20px" }}>
                    {amountField(
                      "totalBankBalanceAmount",
                      String.Dashboard.snapshot.savingBalance,
                    )}
                    {amountField(
                      "totalMutualFundsAmount",
                      String.Dashboard.snapshot.mutualFunds,
                    )}
                    {amountField(
                      "totalStocksAmount",
                      String.Dashboard.snapshot.stocks,
                    )}
                    <FinancialCollectionRow
                      title={SNAPSHOT_FIELDS.depositTitle}
                      count={deposits.length}
                      tooltip={SNAPSHOT_COPY.tooltips.deposits}
                      total={formatToIndianCurrency(
                        financialValues.totalDepositAmount || 0,
                      )}
                      totalLabel={SNAPSHOT_COPY.totalLabel}
                      addLabel={
                        deposits.length > 0
                          ? SNAPSHOT_COPY.editFund
                          : SNAPSHOT_COPY.addFund
                      }
                      onAdd={handleOpenDeposit}
                    />
                    <FinancialCollectionRow
                      title={SNAPSHOT_FIELDS.ProvidentFundTitle}
                      count={pfCount}
                      tooltip={SNAPSHOT_COPY.tooltips.providentFund}
                      total={formatToIndianCurrency(
                        financialValues.providentFundAmount || 0,
                      )}
                      totalLabel={SNAPSHOT_COPY.totalMaturityAmount}
                      addLabel={
                        pfCount > 0
                          ? SNAPSHOT_COPY.editFund
                          : SNAPSHOT_COPY.addProvidentFund
                      }
                      onAdd={handleOpenPF}
                    />
                    <FinancialCollectionRow
                      title={SNAPSHOT_FIELDS.policyTitle}
                      count={policies.length}
                      tooltip={SNAPSHOT_COPY.tooltips.policies}
                      total={formatToIndianCurrency(
                        financialValues.totalPolicyAmount || 0,
                      )}
                      totalLabel={SNAPSHOT_COPY.totalMaturityAmount}
                      addLabel={
                        policies.length > 0
                          ? SNAPSHOT_COPY.editPolicy
                          : SNAPSHOT_COPY.addPolicy
                      }
                      onAdd={handleOpenPolicy}
                    />
                    <FinancialCollectionRow
                      title={SNAPSHOT_FIELDS.npsTitle}
                      tooltip={SNAPSHOT_COPY.tooltips.nps}
                      total={formatToIndianCurrency(
                        financialValues.totalNpsAmount || 0,
                      )}
                      totalLabel={SNAPSHOT_COPY.totalNps}
                      addLabel={
                        npsCount > 0
                          ? SNAPSHOT_COPY.editNps
                          : SNAPSHOT_COPY.addNps
                      }
                      onAdd={handleOpenNPS}
                    />
                  </Box>
                ),
              },
            ]}
          />
        </Box>

        <Box sx={{ mt: "32px", mb: "24px" }}>
          <Button
            buttonType="primary"
            width="100%"
            disabled={!isSnapshotComplete}
            onClick={handleViewDashbord}
          >
            {String.continue}
          </Button>
        </Box>
      </Container>
      <AppToast
        open={snackbarOpen}
        message={snackbarMessage.message || ""}
        handleClose={() => {
          setSnackbarOpen(false);
        }}
        severity={snackbarMessage.severity}
      />

      <DepositDetailsModal
        isOpen={activeModal === "depositForm"}
        onClose={handleCloseDepositForm}
        onSave={handleDepositSubmit}
        initialValue={editIndex === null ? null : deposits[editIndex]}
      />
      <SnapshotCollectionListModal
        isOpen={activeModal === "depositList"}
        onClose={handleCloseDepositList}
        title={SNAPSHOT_COPY.modals.deposit.listTitle}
        totalLabel={SNAPSHOT_COPY.totalMaturityAmount}
        totalAmount={financialValues.totalDepositAmount || 0}
        entries={depositEntries}
        addAnotherLabel={SNAPSHOT_COPY.addAnotherFund}
        onAddAnother={handleAddAnotherDeposit}
        onEdit={handleEditDeposit}
        onDelete={handleDeleteDeposit}
      />

      <PolicyDetailsModal
        isOpen={activeModal === "policyForm"}
        onClose={handleClosePolicyForm}
        onSave={handlePolicySubmit}
        initialValue={editIndex === null ? null : policies[editIndex]}
      />
      <SnapshotCollectionListModal
        isOpen={activeModal === "policyList"}
        onClose={handleClosePolicyList}
        title={SNAPSHOT_COPY.modals.policy.listTitle}
        totalLabel={SNAPSHOT_COPY.totalPolicyCoverage}
        totalAmount={financialValues.totalPolicyAmount || 0}
        entries={policyEntries}
        addAnotherLabel={SNAPSHOT_COPY.addAnotherPolicy}
        onAddAnother={handleAddAnotherPolicy}
        onEdit={handleEditPolicy}
        onDelete={handleDeletePolicy}
      />

      <ProvidentFundDetailsModal
        isOpen={activeModal === "pfForm" || activeModal === "providentFund"}
        onClose={handleClosePFForm}
        onSave={handlePFSubmit}
        initialValue={financialValues.pfList || null}
        initialSegment={editPFKey || null}
      />
      <SnapshotCollectionListModal
        isOpen={activeModal === "pfList"}
        onClose={handleClosePFList}
        title={
          SNAPSHOT_COPY.modals.providentFund.listTitle ||
          "Fund List"
        }
        totalLabel={SNAPSHOT_COPY.totalMaturityAmount}
        totalAmount={financialValues.providentFundAmount || 0}
        entries={pfEntries}
        addAnotherLabel={SNAPSHOT_COPY.addAnotherFund}
        onAddAnother={handleAddAnotherPF}
        onEdit={handleEditPF}
        onDelete={handleDeletePF}
      />
      <NpsDetailsModal
        isOpen={activeModal === "nps"}
        onClose={handleCloseNPS}
        onSave={handleNPSSubmit}
        initialValue={npsList[0] || null}
      />
    </Box>
  );
};

export default FinancialSnapshot;
